export interface WeeklyDigestResponse {
  response_type: 'digest';
  query?: string;
  digest_id?: string;
  start_date: string;
  end_date: string;
  summary: string;
  at_risk_accounts: Array<{
    customer_name: string;
    health_score: number;
    risk_factors: string[];
    mitigation_plan: string;
  }>;
  thriving_accounts: Array<{
    customer_name: string;
    health_score: number;
    success_indicators: string[];
    expansion_opportunities: string;
  }>;
  product_gaps: Array<{
    gap_description: string;
    impact: string;
    customer_examples: string[];
  }>;
  generated_at: string;
}

export interface ProgressSummaryResponse {
  response_type: 'progress';
  query?: string;
  topic: string;
  since_date: string;
  summary: string;
  key_updates: string[];
  blockers: string[];
  decisions: string[];
  next_steps: string[];
  generated_at: string;
}

export interface AccountRadarItem {
  customer_id: string;
  customer_name: string;
  health_score?: number;
  attention_reason: string;
  detail: string;
  evidence: string;
  recommended_action: string;
  priority_score?: number;
}

export interface AccountRadarResponse {
  response_type: 'account_radar';
  query?: string;
  radar_id?: string;
  timeframe: string;
  summary: string;
  items: AccountRadarItem[];
  generated_at: string;
}

export interface RiskItem {
  customer_id: string;
  customer_name: string;
  health_score?: number;
  risk_detected: boolean;
  reason: string;
  evidence: string;
  recommended_action: string;
  narrative?: string;
  priority_score?: number;
}

export interface RiskAssessmentResponse {
  response_type: 'risk_assessment';
  query?: string;
  assessment_id?: string;
  risks: RiskItem[];
  message?: string;
  generated_at: string;
}

export interface AccountNarrativeResponse {
  response_type: 'account_narrative';
  query?: string;
  narrative_id?: string;
  customer_id: string;
  customer_name: string;
  start_date: string;
  end_date: string;
  narrative: string;
  value_delivered: string[];
  whats_changed: string[];
  recommended_next: string[];
  evidence: string[];
  generated_at: string;
}

export interface EmailDraftResponse {
  response_type: 'email_draft';
  draft_id?: string;
  customer_id: string;
  customer_name: string;
  email_type: string;
  subject: string;
  body: string;
  recipients?: string[];
  created_at?: string;
}

export function isDigestResponse(data: any): data is WeeklyDigestResponse {
  return data?.response_type === 'digest' ||
    (data?.at_risk_accounts !== undefined && data?.thriving_accounts !== undefined);
}

export function isProgressResponse(data: any): data is ProgressSummaryResponse {
  return data?.response_type === 'progress' ||
    (data?.key_updates !== undefined && data?.blockers !== undefined && data?.decisions !== undefined);
}

export function isRadarResponse(data: any): data is AccountRadarResponse {
  return data?.response_type === 'account_radar';
}

export function isRiskResponse(data: any): data is RiskAssessmentResponse {
  return data?.response_type === 'risk_assessment';
}

export function isNarrativeResponse(data: any): data is AccountNarrativeResponse {
  return data?.response_type === 'account_narrative';
}

export function isEmailDraftResponse(data: any): data is EmailDraftResponse {
  return data?.response_type === 'email_draft';
}

export function isRAGResponse(data: any): boolean {
  return data?.response?.analysis !== undefined || data?.response?.key_insights !== undefined;
}
